import React, { useEffect, useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  makeStyles,
} from "@material-ui/core";
import { toast } from "react-toastify";

import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import toastError from "../../errors/toastError";
import {
  listBoards,
  showBoard,
  moveTicketToLane,
} from "../../services/kanbanEnterpriseService";

const useStyles = makeStyles((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    minWidth: 320,
    paddingTop: theme.spacing(0.5),
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: 8,
    flexShrink: 0,
    display: "inline-block",
  },
  empty: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontSize: "0.85rem",
  },
  loadingRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1, 0),
  },
}));

const sameId = (a, b) =>
  a != null && b != null && a !== "" && b !== "" && Number(a) === Number(b);

const deriveUpdateData = (column) => {
  const filter = column?.filterJson || {};
  const updateData = {};

  if (
    column?.type === "status" &&
    Array.isArray(filter.status) &&
    filter.status.length === 1
  ) {
    updateData.status = filter.status[0];
  }

  if (
    column?.type === "queue" &&
    Array.isArray(filter.queueIds) &&
    filter.queueIds.length === 1
  ) {
    updateData.queueId = filter.queueIds[0];
  }

  if (
    column?.type === "assignee" &&
    Array.isArray(filter.userIds) &&
    filter.userIds.length === 1
  ) {
    updateData.assigneeId = filter.userIds[0];
  }

  return updateData;
};

let enterpriseFlagCache = {
  companyId: null,
  value: null,
  promise: null,
};

export async function resolveKanbanEnterpriseEnabled(companyId, getPlanCompany, ticket) {
  const flagFromTicket = ticket?.company?.plan?.useKanbanEnterprise;
  if (typeof flagFromTicket === "boolean") {
    return flagFromTicket;
  }

  if (!companyId || !getPlanCompany) return false;

  if (
    enterpriseFlagCache.companyId === companyId &&
    typeof enterpriseFlagCache.value === "boolean"
  ) {
    return enterpriseFlagCache.value;
  }

  if (
    enterpriseFlagCache.companyId === companyId &&
    enterpriseFlagCache.promise
  ) {
    return enterpriseFlagCache.promise;
  }

  enterpriseFlagCache.companyId = companyId;
  enterpriseFlagCache.promise = getPlanCompany(undefined, companyId)
    .then((planConfigs) => {
      const enabled = !!planConfigs?.plan?.useKanbanEnterprise;
      enterpriseFlagCache.value = enabled;
      enterpriseFlagCache.promise = null;
      return enabled;
    })
    .catch(() => {
      enterpriseFlagCache.value = false;
      enterpriseFlagCache.promise = null;
      return false;
    });

  return enterpriseFlagCache.promise;
}

export function useKanbanEnterpriseEnabled(ticket) {
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let mounted = true;
    resolveKanbanEnterpriseEnabled(user?.companyId, getPlanCompany, ticket).then(
      (value) => {
        if (mounted) setEnabled(!!value);
      }
    );
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId, ticket?.id, ticket?.company?.plan?.useKanbanEnterprise]);

  return enabled;
}

/**
 * Modal para escolher Quadro + Etapa do Kanban Enterprise a partir do menu do ticket.
 */
export default function KanbanEnterpriseMoveModal({ open, onClose, ticket }) {
  const classes = useStyles();
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [columns, setColumns] = useState([]);
  const [selectedColumnId, setSelectedColumnId] = useState("");
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let mounted = true;

    const load = async () => {
      setLoadingBoards(true);
      try {
        const data = await listBoards();
        if (!mounted) return;
        const list = Array.isArray(data) ? data : [];
        setBoards(list);

        const currentColId =
          ticket?.kanbanColumnId ?? ticket?.kanbanColumn?.id;
        let initialBoard = null;
        if (currentColId) {
          initialBoard = list.find((b) =>
            (b.columns || []).some((c) => sameId(c.id, currentColId))
          );
          if (!initialBoard && ticket?.kanbanColumn?.boardId) {
            initialBoard = list.find((b) =>
              sameId(b.id, ticket.kanbanColumn.boardId)
            );
          }
        }
        if (!initialBoard) initialBoard = list[0];
        setSelectedBoardId(initialBoard ? initialBoard.id : "");
      } catch (err) {
        if (mounted) toastError(err);
      } finally {
        if (mounted) setLoadingBoards(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticket?.id]);

  useEffect(() => {
    if (!open || !selectedBoardId) {
      setColumns([]);
      setSelectedColumnId("");
      return undefined;
    }
    let mounted = true;

    const load = async () => {
      setLoadingColumns(true);
      try {
        const board = await showBoard(selectedBoardId);
        if (!mounted) return;
        const cols = (board?.columns || [])
          .slice()
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        setColumns(cols);

        const currentColId =
          ticket?.kanbanColumnId ?? ticket?.kanbanColumn?.id;
        const matched = cols.find((c) => sameId(c.id, currentColId));
        setSelectedColumnId(matched ? matched.id : "");
      } catch (err) {
        if (mounted) toastError(err);
      } finally {
        if (mounted) setLoadingColumns(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedBoardId, ticket?.id]);

  const handleClose = () => {
    if (moving) return;
    onClose();
  };

  const handleMove = async () => {
    if (!ticket?.id || !selectedColumnId) {
      toast.warning("Selecione o quadro e a etapa");
      return;
    }

    const column = columns.find((c) => sameId(c.id, selectedColumnId));
    if (!column) return;

    setMoving(true);
    try {
      const updateData = deriveUpdateData(column);
      await moveTicketToLane(ticket.id, {
        columnId: Number(column.id),
        updateData,
      });
      toast.success(`Ticket movido para "${column.name}"`);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setMoving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Kanban Enterprise</DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          {loadingBoards ? (
            <div className={classes.loadingRow}>
              <CircularProgress size={18} />
              <Typography variant="body2">Carregando quadros...</Typography>
            </div>
          ) : boards.length === 0 ? (
            <Typography className={classes.empty}>
              Nenhum quadro do Kanban Enterprise configurado.
            </Typography>
          ) : (
            <>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="ke-move-board-label">Quadro</InputLabel>
                <Select
                  labelId="ke-move-board-label"
                  label="Quadro"
                  value={selectedBoardId || ""}
                  onChange={(e) => setSelectedBoardId(e.target.value)}
                  disabled={moving}
                >
                  {boards.map((board) => (
                    <MenuItem key={board.id} value={board.id}>
                      {board.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="ke-move-stage-label">Etapa</InputLabel>
                <Select
                  labelId="ke-move-stage-label"
                  label="Etapa"
                  value={selectedColumnId || ""}
                  onChange={(e) => setSelectedColumnId(e.target.value)}
                  disabled={moving || loadingColumns || columns.length === 0}
                  renderValue={(value) => {
                    const column = columns.find((c) => sameId(c.id, value));
                    if (!column) return "Selecione uma etapa";
                    return (
                      <Box display="flex" alignItems="center">
                        <span
                          className={classes.colorDot}
                          style={{
                            backgroundColor: column.color || "#757575",
                          }}
                        />
                        {column.name}
                      </Box>
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Selecione uma etapa</em>
                  </MenuItem>
                  {columns.map((column) => (
                    <MenuItem key={column.id} value={column.id}>
                      <span
                        className={classes.colorDot}
                        style={{
                          backgroundColor: column.color || "#757575",
                        }}
                      />
                      {column.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(loadingColumns || moving) && (
                <div className={classes.loadingRow}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">
                    {moving ? "Movendo ticket..." : "Carregando etapas..."}
                  </Typography>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={moving}>
          Cancelar
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleMove}
          disabled={
            moving ||
            loadingBoards ||
            loadingColumns ||
            !selectedColumnId ||
            boards.length === 0
          }
        >
          {moving ? "Movendo..." : "Mover"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
